import { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  copied: boolean;
}

/** Global error boundary — catches React rendering errors and shows a recovery UI. */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, copied: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, copied: false };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[ErrorBoundary] Caught error:', error, info.componentStack);
  }

  private copyError = () => {
    const details = `Error: ${this.state.error?.message}\n\nStack: ${this.state.error?.stack ?? 'N/A'}\n\nURL: ${window.location.href}\nTime: ${new Date().toISOString()}`;
    navigator.clipboard.writeText(details).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }).catch(() => {});
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#0f0f1a]">
          <div className="card max-w-md w-full text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-gray-400 text-sm mb-6">
              {this.state.error?.message || 'An unexpected error occurred in the application.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button
                  onClick={() => window.history.back()}
                  className="btn-primary"
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  Go Back
                </button>
                <button
                  onClick={() => { window.location.href = '/startups'; }}
                  className="btn-primary"
                >
                  Go to Dashboard
                </button>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button
                  onClick={() => window.location.reload()}
                  className="btn-primary"
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', fontSize: 13 }}
                >
                  Reload page
                </button>
                <button
                  onClick={this.copyError}
                  className="btn-primary"
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', fontSize: 13 }}
                >
                  {this.state.copied ? 'Copied!' : 'Copy error details'}
                </button>
              </div>
              <p className="text-gray-500 text-xs mt-2">
                Need help? Email <a href="mailto:support@startupscore.live" style={{ color: '#7c6ff7' }}>support@startupscore.live</a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
